import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { ScheduleDefaults, ScheduleQueries, ScheduleTypes } from '@frontend/api-schedule';
import { useTranslation } from '@frontend/i18n';
import { BOOKING_SITE_TRACKING_IDS } from '@frontend/schedule-helpers';
import { CheckboxField, ResolvedFieldProps, Text } from '@frontend/design-system';
import { useAutoFetchAndSetCaptchaV3Token } from './captcha';
import { getComplete, RequestContact } from './request-contact.component';
import { BOOKING_SITE_HTTP_OPTIONS, getBookingSiteFieldDefaults } from './utils';

function tryParseJson<T>(jsonString?: string): T | undefined {
  try {
    return JSON.parse(jsonString ?? '');
  } catch (error) {
    return undefined;
  }
}

export interface InfoFormComponentProps {
  locationId: string;
  locationName: string;
  formDetails: ScheduleTypes.FormDetails;
  setFormDetails: Dispatch<SetStateAction<ScheduleTypes.FormDetails>>;
  complianceConsent?: ResolvedFieldProps<'checkbox'>;
  isV3CaptchaEnabled: boolean;
  isIntegratedOffice?: boolean;
}

// NOTE: This is copied from booking site and needs refactoring and changes later
export const InfoFormComponent = ({
  locationId,
  locationName,
  formDetails,
  setFormDetails,
  complianceConsent,
  isV3CaptchaEnabled,
  isIntegratedOffice = true,
}: InfoFormComponentProps) => {
  const { t } = useTranslation('schedule-patient-info-form');
  const { data } = ScheduleQueries.useGetCustomFields(locationId, BOOKING_SITE_HTTP_OPTIONS);
  useAutoFetchAndSetCaptchaV3Token({ isEnabled: isV3CaptchaEnabled });

  const { customFields, overrideFields } = useMemo(() => {
    const overrideFieldDefaults = getBookingSiteFieldDefaults(isIntegratedOffice);

    return {
      customFields: tryParseJson<ScheduleTypes.CustomFields>(data?.customFields),
      overrideFields: data?.overrideFields
        ? tryParseJson<ScheduleTypes.OverrideFields>(data?.overrideFields)
        : overrideFieldDefaults,
    };
  }, [data, isIntegratedOffice]);

  // TODO: This is a temporary fix to make the address field required for integrated locations only, until we understand which PMSs require a patient address when creating an appointment from this request. Currently sometimes failing `desktop/v1/syncapp-writebacks/customer` due to a missing address.
  if (isIntegratedOffice) {
    if (overrideFields && overrideFields.address) {
      overrideFields.address = {
        ...ScheduleDefaults.overrideFields.address,
      };
    }
  }

  const handlePatientInfoChange = useCallback(
    (patientInfo: Partial<ScheduleTypes.PatientInfo>, formErrors?: Record<string, string>) => {
      setFormDetails?.((prev) => {
        const input = document.getElementsByTagName('input');
        const otherFormValues = Object.values(input).reduce((acc, field) => {
          const name = field.name as keyof ScheduleTypes.PatientInfo;
          if (patientInfo?.[name] || !field.value || !field.required) return acc;
          return { ...acc, [field.name]: field.value };
        }, {} as Partial<ScheduleTypes.PatientInfo>);

        const completeFormValues = { ...patientInfo, ...otherFormValues } as Partial<ScheduleTypes.PatientInfo>;
        const { isComplete, errors } = getComplete(completeFormValues, overrideFields, customFields, formErrors);

        const merged = { ...prev?.patientInfo, ...patientInfo } as ScheduleTypes.PatientInfo;
        return { ...prev, patientInfo: merged, formErrors: errors, formComplete: isComplete };
      });
    },
    [setFormDetails, overrideFields, customFields]
  );

  const handleError = useCallback(
    (formErrors?: Record<string, string>) => {
      setFormDetails?.((prev) => {
        const errors = { ...prev?.formErrors, ...formErrors };
        return { ...prev, formErrors: errors };
      });
    },
    [setFormDetails]
  );

  return (
    <>
      <RequestContact
        customFields={customFields}
        overrideFields={overrideFields}
        value={formDetails?.patientInfo}
        errors={formDetails?.formErrors}
        onChange={handlePatientInfoChange}
        onError={handleError}
      />
      <section css={consentStyle}>
        {complianceConsent && (
          <CheckboxField
            {...complianceConsent}
            name='10dlc-compliance-consent'
            label='*'
            trackingId={BOOKING_SITE_TRACKING_IDS.patientInfoStep.concentCheckbox}
          />
        )}
        <Text>
          {t(
            "I understand that this form is using Google's reCaptcha to verify requests. By providing your phone number, you consent to receiving SMS text messages and phone calls from {{officeName}} – Online Scheduling and Text Connect, including texts and calls that promote our products and services. Text messages and data rates may apply. You may withdraw your consent at any time. Reply STOP to any text message to unsubscribe, or HELP for more options.",
            { officeName: locationName }
          )}
        </Text>
      </section>
    </>
  );
};
const consentStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--fabric-space-gap-sm)',
});
